import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, MessageCircle, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatMoney } from "@/lib/api";
import { usePublicConfig } from "@/context/PublicConfigContext";
import { trackEvent } from "./LandingPublic";
import { fireWhatsappCompraYaConversion } from "@/lib/gtag";
import { toast } from "sonner";

export default function Carrito() {
  const { items, updateQty, remove, totalMonto, clear } = useCart();
  const navigate = useNavigate();
  const { whatsapp } = usePublicConfig();
  const [form, setForm] = useState({ nombre: "", telefono: "", direccion: "", notas: "" });

  const buildMessage = () => {
    let msg = `*🛒 NUEVO PEDIDO – CASACOMPLETA.COM*\n\n`;
    msg += `*Cliente:* ${form.nombre || "(sin nombre)"}\n`;
    if (form.telefono) msg += `*Teléfono:* ${form.telefono}\n`;
    if (form.direccion) msg += `*Dirección:* ${form.direccion}\n`;
    msg += `\n*Productos:*\n`;
    items.forEach((it, i) => {
      const variants = [it.color && `Color: ${it.color}`, it.tela && `Tela: ${it.tela}`, it.tamano && `Tamaño: ${it.tamano}`, it.estilo && `Estilo: ${it.estilo}`].filter(Boolean).join(" · ");
      msg += `\n${i + 1}. *${it.nombre}*\n`;
      if (variants) msg += `   ${variants}\n`;
      if (it.en_oferta && it.precio_normal && it.precio_normal > it.precio) {
        msg += `   Antes ~${formatMoney(it.precio_normal)}~ · OFERTA ${formatMoney(it.precio)}\n`;
      }
      msg += `   ${it.cantidad} × ${formatMoney(it.precio)} = *${formatMoney(it.cantidad * it.precio)}*\n`;
    });
    msg += `\n---\n*TOTAL: ${formatMoney(totalMonto)}*\n`;
    if (form.notas) msg += `\n*Notas:* ${form.notas}\n`;
    msg += `\n_Enviado desde casacompleta.com_`;
    return msg;
  };

  const enviarWhatsApp = () => {
    if (items.length === 0) return toast.error("Tu carrito está vacío");
    if (!form.nombre.trim()) return toast.error("Ingresa tu nombre");
    if (!form.telefono.trim()) return toast.error("Ingresa tu teléfono");
    const url = `https://wa.me/${(whatsapp || "50243036012").replace(/\D/g, "")}?text=${encodeURIComponent(buildMessage())}`;
    trackEvent("checkout_whatsapp", { monto: totalMonto, extra: { items: items.length, cliente: form.nombre } });
    // Google Ads: conversión "Clic WhatsApp - Compra ya" (única, con fallback si gtag falla).
    fireWhatsappCompraYaConversion(url);
    toast.success("Abriendo WhatsApp con tu pedido…");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-zinc-600 hover:text-[#E30613] mb-6" data-testid="back-to-tienda">
        <ArrowLeft className="w-4 h-4" /> Seguir comprando
      </Link>

      <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight">Tu Carrito</h1>
      <div className="h-1 w-20 bg-[#E30613] mt-2" />

      {items.length === 0 ? (
        <div className="mt-8 text-center py-16 bg-zinc-50 border-2 border-dashed border-zinc-300" data-testid="cart-empty">
          <p className="text-zinc-500 font-medium mb-4">Tu carrito está vacío</p>
          <Link to="/" className="inline-block bg-[#FFD100] hover:bg-black hover:text-[#FFD100] text-black font-black px-6 py-3 uppercase tracking-widest text-sm border-2 border-black transition-colors">
            Ir a la tienda
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => (
              <div key={it.key} className="bg-white border-2 border-zinc-200 p-3 flex gap-3" data-testid={`cart-item-${it.id}`}>
                <div className="w-20 h-20 md:w-24 md:h-24 bg-zinc-100 flex-shrink-0 overflow-hidden">
                  {it.imagen ? <img src={it.imagen} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm md:text-base line-clamp-2">{it.nombre}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {[it.color, it.tela, it.tamano, it.estilo].filter(Boolean).join(" · ") || "Sin variantes"}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border-2 border-black">
                      <button onClick={() => updateQty(it.key, it.cantidad - 1)} className="w-8 h-8 font-black hover:bg-[#FFD100]">−</button>
                      <span className="w-10 h-8 flex items-center justify-center border-x-2 border-black text-sm font-black">{it.cantidad}</span>
                      <button onClick={() => updateQty(it.key, it.cantidad + 1)} className="w-8 h-8 font-black hover:bg-[#FFD100]">+</button>
                    </div>
                    <div className="text-right">
                      {it.en_oferta && it.precio_normal > it.precio && (
                        <div className="text-[10px] text-zinc-400 line-through leading-none">{formatMoney(it.precio_normal * it.cantidad)}</div>
                      )}
                      <div className="font-black text-[#E30613] text-lg leading-tight">{formatMoney(it.cantidad * it.precio)}</div>
                      {it.en_oferta && <div className="text-[9px] bg-[#E30613] text-white px-1 py-0.5 uppercase font-black tracking-widest inline-block">Oferta</div>}
                    </div>
                    <button onClick={() => remove(it.key)} className="text-zinc-400 hover:text-[#E30613] p-2" data-testid={`cart-remove-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Checkout */}
          <div className="bg-black text-white p-6 sticky top-24 self-start">
            <h2 className="text-xl font-black uppercase tracking-widest text-[#FFD100] mb-4">Tus datos</h2>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nombre completo *"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full h-11 px-3 bg-white text-black text-sm font-medium focus:outline-none border-2 border-transparent focus:border-[#FFD100]"
                data-testid="cart-nombre"
              />
              <input
                type="tel"
                placeholder="Teléfono *"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="w-full h-11 px-3 bg-white text-black text-sm font-medium focus:outline-none border-2 border-transparent focus:border-[#FFD100]"
                data-testid="cart-telefono"
              />
              <input
                type="text"
                placeholder="Dirección de entrega"
                value={form.direccion}
                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                className="w-full h-11 px-3 bg-white text-black text-sm font-medium focus:outline-none border-2 border-transparent focus:border-[#FFD100]"
                data-testid="cart-direccion"
              />
              <textarea
                placeholder="Notas adicionales (opcional)"
                value={form.notas}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-white text-black text-sm font-medium focus:outline-none border-2 border-transparent focus:border-[#FFD100] resize-none"
                data-testid="cart-notas"
              />
            </div>
            <div className="mt-6 pt-4 border-t border-zinc-700 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-zinc-400">Subtotal:</span>
                <span className="font-black text-lg text-white">{formatMoney(totalMonto)}</span>
              </div>
              <div className="text-[11px] text-zinc-500">
                El envío se coordina por WhatsApp según tu ubicación.
              </div>
            </div>
            <button
              onClick={enviarWhatsApp}
              className="mt-4 w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-black uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-2 transition-all"
              data-testid="btn-order-whatsapp"
            >
              <MessageCircle className="w-5 h-5" /> Ordenar por WhatsApp
            </button>
            <button
              onClick={() => { clear(); toast.info("Carrito vaciado"); }}
              className="mt-2 w-full text-xs text-zinc-500 hover:text-white font-medium py-2"
            >
              Vaciar carrito
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
